/* istanbul ignore file */
import React, { useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import { Box } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import useAuthenticationService from 'infrastructure/security/authentication/authenticationService';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { ONE_STOP_PATH } from 'domain/shared/oneStop';
import Logo from 'assets/images/logo.svg';
import { useDateService, useDeviceDetectorService, MAX_LG_WIDTH, useAsyncValue } from 'framework/utils';
import { USER_SETTINGS_PATH } from 'settings/UserSettingsRouter';
/* @ts-ignore */
import { getTimezoneWidget } from '@timezone-change-widget/timezone-change-widget';
import '@timezone-change-widget/timezone-change-widget/build/styles.css';
import { MainNavigationTabs } from 'framework/ui/menu';
import { ScrollToTop } from 'framework/ui/ScrollToTop';
import navigationConfig from 'app/navigationConfig';
import { HANDBOOK_PATH } from 'domain/shared/core/sharedRouteConstants';
import { COLLAPSED_WIDTH, DRAWER_WIDTH } from './AppSidebarNav/AppSidebarNav';
import { SidebarProvider, useSidebarContext } from 'framework/ui/layout';
import SidebarNav from './AppSidebarNav/SidebarNav';
import ErrorBoundarySupportAction from 'domain/gpo/oneStop/feedbackModal/ErrorBoundarySupportAction';
import { ErrorBoundary } from 'framework/ui/ErrorBoundary';
import { MIN_SUPPORTED_BROWSER_VERSIONS } from 'infrastructure/browserConfig';
import DefaultHeader from './DefaultHeader/DefaultHeader';
import DefaultFooter from './DefaultFooter';
import { ARTIFACT_STORAGE_PATH } from 'domain/shared/artifactStorage';
import { fadeInSx } from 'framework/ui/styles';

interface DefaultLayoutProps {
  children?: React.ReactNode;
}

/**
 * DefaultLayout
 * - UI layout component. Reads mobileOpen/collapsed/handleDrawerToggle from context returned by AppSidebarProvider.
 * - If context is not present, falls back to defaults so it remains robust.
 */
const DefaultLayout: React.FC<DefaultLayoutProps> = ({ children }) => {
  const { isAuthenticated } = useAuthenticationService();
  const isUserAuthenticated = useAsyncValue(isAuthenticated);
  const { getUserTimezone } = useDateService();
  const userTimezone = getUserTimezone();
  const { pathname } = useLocation();

  const { handleDrawerToggle, collapsed } = useSidebarContext?.() ?? {};

  useEffect(() => {
    if (isUserAuthenticated) {
      getTimezoneWidget(userTimezone, USER_SETTINGS_PATH);
    }
  }, [userTimezone, isUserAuthenticated, pathname]);

  if (isUserAuthenticated === null) {
    return null;
  }

  // compute left offset depending on collapsed state when on large screens
  // this mirrors what the Sidebar uses (DRAWER_WIDTH / COLLAPSED_WIDTH)
  const leftOffset = isUserAuthenticated ? (collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH) : '0px';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', flexGrow: 1, position: 'relative' }}>
        <Box display="flex">
          {isUserAuthenticated ? (
            <SidebarNav navigationConfig={navigationConfig} exceptions={[ARTIFACT_STORAGE_PATH, HANDBOOK_PATH]} />
          ) : null}
        </Box>

        <Box
          display="flex"
          sx={{
            width: {
              xs: '100%',
              lg: `calc(100% - ${leftOffset})`,
            },
          }}
        >
          <AppBar
            elevation={0}
            position="fixed"
            sx={{
              left: {
                xs: 0,
                lg: leftOffset,
              },
              minHeight: { sm: isUserAuthenticated ? '68px' : '50px' },
              justifyContent: 'center',
              width: {
                // on lg reduce width by leftOffset so AppBar doesn't overlap fixed Sidebar
                lg: isUserAuthenticated ? `calc(100% - ${leftOffset})` : '100%',
              },
              transition: 'margin-left 200ms ease, width 200ms ease',
            }}
          >
            <Toolbar>
              {isUserAuthenticated && (
                <IconButton
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: '12px', display: { lg: 'none' } }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Box sx={{ display: { xs: 'block', md: 'none' }, marginRight: '20px' }}>
                <Link to={ONE_STOP_PATH}>
                  <img src={Logo} alt="Syberry logo" width={75} />
                </Link>
              </Box>
              <DefaultHeader />
            </Toolbar>
          </AppBar>

          <Box
            component="main"
            sx={{
              ...fadeInSx,
              flex: '1 1 auto',
              minWidth: 0,
              p: 1,
              // reserve top space for AppBar and keep it consistent with AppBar sizing logic
              marginTop: { xs: isUserAuthenticated ? '60px' : '50px', sm: '68px' },
              // on large screens shift content by leftOffset to avoid overlay by fixed sidebar
              transition: 'margin-left 200ms ease',
            }}
          >
            <ErrorBoundary
              minBrowserVersions={MIN_SUPPORTED_BROWSER_VERSIONS}
              renderSupportAction={description => <ErrorBoundarySupportAction description={description} />}
            >
              <>
                <MainNavigationTabs />
                {/* Outlet renders routed content */}
                <Outlet />
                {children}
              </>
            </ErrorBoundary>
          </Box>
        </Box>
      </Box>

      <DefaultFooter />
      <ScrollToTop />
    </Box>
  );
};

/**
 * DefaultLayoutWrapper
 * - Provides AppSidebarProvider and manages mobileOpen / collapsed state.
 * - Use this as the top-level layout component (it renders DefaultLayout inside provider).
 *
 * Behavior:
 * - mobileOpen: boolean, toggled by handleDrawerToggle (used for temporary drawer on mobile)
 * - collapsed: boolean, toggled by setCollapsed (used for permanent drawer on desktop)
 *
 * Note:
 * - We intentionally keep collapsed in this provider so AppSidebarNav (which consumes context)
 *   and DefaultLayout (which also consumes) stay in sync.
 */
const DefaultLayoutWrapper: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthenticationService();
  const isUserAuthenticated = useAsyncValue(isAuthenticated);
  const { isDesktop } = useDeviceDetectorService({ maxWidth: MAX_LG_WIDTH, defaultValue: false });
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState<boolean>(false);

  const handleDrawerToggle = React.useCallback(() => {
    setMobileOpen(s => !s);
  }, []);

  // If the user is not authenticated we might want to close mobile drawer
  useEffect(() => {
    if (!isUserAuthenticated) {
      setMobileOpen(false);
    }
  }, [isUserAuthenticated]);

  const providerValue = React.useMemo(
    () => ({
      isDesktop,
      mobileOpen,
      handleDrawerToggle,
      collapsed,
      setCollapsed,
    }),
    [mobileOpen, handleDrawerToggle, collapsed, isDesktop],
  );

  return (
    <SidebarProvider value={providerValue}>
      <DefaultLayout>{children}</DefaultLayout>
    </SidebarProvider>
  );
};

export default DefaultLayoutWrapper;
